/**
 * DesktopSidebar - Sidebar professionale per navigazione desktop
 * 
 * Caratteristiche:
 * - Sezioni organizzate logicamente
 * - Badge notifiche
 * - Evidenziazione sezione attiva
 * - Espandibile per sezione admin
 */

 import { Link, useLocation, useNavigate } from 'react-router-dom';
 import { useAuth, useIsAuthenticated } from '../stores/auth';
 import { useDeviceMode } from '../hooks/useDeviceMode';
 import { useState } from 'react';
 import { fixImageUrl } from '../utils/images';
 import {
   ShipIcon,
   MapPinLucideIcon,
   ChatIcon,
   ListIcon,
   PlusLucideIcon,
   BarChartLucideIcon,
   ShieldLucideIcon,
   SettingsIcon,
   BookLucideIcon,
   LogOutLucideIcon,
   ChevronDownIcon,
   ArrowRightLucideIcon,
   DollarSignLucideIcon,
   TrendingUpLucideIcon,
   UsersLucideIcon,
   PackageLucideIcon,
   DatabaseLucideIcon,
   ActivityLucideIcon,
   FileLucideIcon,
   ImageLucideIcon,
   ChevronLeftIcon,
   BuildingLucideIcon,
   ZapLucideIcon,
   StarLucideIcon,
   UserLucideIcon,
 } from './Icons';
 import { useGlassmorphism } from '../hooks/useGlassmorphism';
 import { useTheme } from '../hooks/useTheme';
 import { useThemeStyled } from '../hooks/useThemeStyled';
 import GlobalSearch from './GlobalSearch';

interface DesktopSidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  color?: string;
  children?: NavItem[];
}

interface NavSection {
  title: string;
  icon?: React.ElementType;
  items: NavItem[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export default function DesktopSidebar({ isCollapsed = false, onToggle }: DesktopSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const isAdmin = user?.is_admin === true;
  const isDealer = !!user && !isAdmin;
  const isPublic = !isAuthenticated;
  const { isPWA } = useDeviceMode();
  const mapPath = isPWA ? '/map-explorer-pwa' : '/map-explorer-desktop';
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Use theme styled for sidebar CSS variables
  const { sidebar: sidebarVars, colors } = useThemeStyled();
  
  // Theme values for dynamic styling - now using CSS variables!
  const sidebarStyle = {
    background: 'var(--glass-sidebar-bg, var(--color-bg-primary, #fff))',
    borderRight: 'var(--glass-sidebar-border, var(--color-border, #e5e7eb))',
    width: isCollapsed ? sidebarVars.collapsedWidth : sidebarVars.width,
  };
  
  const menuItemStyle = {
    color: 'var(--glass-sidebar-text-color, var(--color-text, #242424))',
    hoverBackground: 'var(--glass-sidebar-hover-bg, var(--color-bg-secondary, #f3f4f6))',
    activeColor: 'var(--glass-sidebar-active-text-color, var(--color-primary, #0B3D6B))',
    activeBackground: 'var(--glass-sidebar-active-bg, var(--color-primary, #0B3D6B20))',
    padding: 'var(--sidebar-padding, 12px 16px)',
  };
  
  const iconStyle = {
    color: 'var(--glass-sidebar-icon-color, var(--color-text-secondary, #898989))',
    activeColor: 'var(--glass-sidebar-active-icon-color, var(--color-primary, #0B3D6B))',
  };
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    admin: true, // Admin section expanded by default if admin
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Definizione sezioni di navigazione - diverse per pubblico vs autenticato
    const sections: NavSection[] = isPublic ? [
      {
        title: 'Navigazione',
        items: [
          { to: '/boats', label: 'Barche', icon: ShipIcon, color: 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' },
          { to: '/motors', label: 'Motori', icon: ZapLucideIcon, color: 'bg-orange-100 text-orange-600' },
          { to: '/accessories', label: 'Accessori', icon: PackageLucideIcon, color: 'bg-purple-100 text-purple-600' },
          { to: mapPath, label: 'Mappa Esplora', icon: MapPinLucideIcon, color: 'bg-green-100 text-green-600' },
          { to: '/general-map', label: 'Mappa Generale', icon: MapPinLucideIcon, color: 'bg-blue-100 text-blue-600' },
        ]
      },
      {
        title: 'Info',
        items: [
          { to: '/manuale', label: 'Guida', icon: BookLucideIcon, color: 'bg-teal-100 text-teal-600' },
          { to: '/portfolio', label: 'Portfolio', icon: StarLucideIcon, color: 'bg-yellow-100 text-yellow-600' },
        ]
      },
    ] : [
      {
        title: 'Navigazione',
        items: [
          { to: '/boats', label: 'Barche', icon: ShipIcon, color: 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' },
          { to: '/motors', label: 'Motori', icon: ZapLucideIcon, color: 'bg-orange-100 text-orange-600' },
          { to: '/accessories', label: 'Accessori', icon: PackageLucideIcon, color: 'bg-purple-100 text-purple-600' },
          { to: mapPath, label: 'Mappa Esplora', icon: MapPinLucideIcon, color: 'bg-green-100 text-green-600' },
          { to: '/general-map', label: 'Mappa Generale', icon: MapPinLucideIcon, color: 'bg-blue-100 text-blue-600' },
        ]
      },
     {
       title: 'Gestione',
       items: [
         { to: '/my-boats', label: 'Le Mie Barche', icon: ListIcon, color: 'bg-emerald-100 text-emerald-600' },
         { to: '/my-motors', label: 'I Miei Motori', icon: ZapLucideIcon, color: 'bg-emerald-100 text-emerald-600' },
         { to: '/my-accessories', label: 'I Miei Accessori', icon: PackageLucideIcon, color: 'bg-purple-100 text-purple-600' },
         { to: '/add-boat', label: 'Aggiungi Barca', icon: PlusLucideIcon, color: 'bg-purple-100 text-purple-600' },
         { to: '/add-motor', label: 'Aggiungi Motore', icon: PlusLucideIcon, color: 'bg-purple-100 text-purple-600' },
         { to: '/add-accessory', label: 'Aggiungi Accessorio', icon: PlusLucideIcon, color: 'bg-indigo-100 text-indigo-600' },
       ]
     },
    {
      title: 'Comunicazione',
      items: [
        { to: isAdmin ? '/admin?tab=whatsapp' : '/dashboard?tab=whatsapp', label: 'WhatsApp', icon: ChatIcon, color: 'bg-green-100 text-green-600' },
        { to: '/trades', label: 'Trattative', icon: DollarSignLucideIcon, color: 'bg-amber-100 text-amber-600' },
      ]
    },
    {
      title: 'Admin',
      icon: ShieldLucideIcon,
      collapsible: true,
      defaultExpanded: isAdmin,
      items: isAdmin ? [
        { to: '/admin', label: 'Dashboard Admin', icon: BarChartLucideIcon, color: 'bg-red-100 text-red-600' },
        { to: '/dealers', label: 'Commercianti', icon: UsersLucideIcon, color: 'bg-sky-100 text-sky-700' },
        { to: '/import-data', label: 'Import Dati', icon: DatabaseLucideIcon, color: 'bg-violet-100 text-violet-600' },
        { to: '/tracking-stats', label: 'Statistiche', icon: ActivityLucideIcon, color: 'bg-orange-100 text-orange-600' },
      ] : []
    },
{
      title: 'Network',
      items: [
        { to: '/dealers', label: 'Commercianti', icon: UsersLucideIcon, color: 'bg-sky-100 text-sky-700' },
      ]
    },
    ...(isDealer ? [{
      title: 'Dashboard',
      icon: TrendingUpLucideIcon,
      collapsible: false,
      defaultExpanded: true,
      items: [
        { to: '/dashboard', label: 'Dashboard Dealer', icon: TrendingUpLucideIcon, color: 'bg-cyan-100 text-cyan-600' },
      ]
    }] : []),
  ];

  // Filtra sezioni vuote
  const activeSections = sections.filter(section => {
    if (section.title === 'Admin' && !isAdmin) return false;
    if (section.collapsible && section.items.length === 0) return false;
    return true;
  });

  if (isCollapsed) {
    return (
      <div className="w-16 h-screen flex flex-col glass-d-sidebar">
        {/* Logo */}
        <div 
          className="h-16 flex items-center justify-center"
          style={{
            background: 'var(--color-primary)'
          }}
        >
          <Link to="/boats">
            <img 
              src={((window as any).BOAT_TRADER_API?.plugin_url || '') + 'sealevel.webp' || '/wp-content/plugins/boat-trader-v3/assets/images/sealevel.webp'}
              alt="SeaLevel" 
              className="h-10 w-auto object-contain"
              width={100}
              height={40}
            />
          </Link>
        </div>

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className="h-10 flex items-center justify-center hover:opacity-80"
          title="Espandi menu"
          aria-label="Espandi menu"
        >
          <ArrowRightLucideIcon className="w-5 h-5 text-gray-500" aria-hidden="true" />
        </button>

        {/* Collapsed Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {sections.flatMap(section => section.items).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center justify-center py-3 mx-2 rounded-lg transition-colors ${
                isActive(item.to) 
                  ? 'bg-sky-50 text-sky-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={item.label}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color || ''}`}>
                <item.icon className="w-5 h-5" />
              </div>
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div 
          className="p-2 flex-shrink-0"
        >
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center py-3 rounded-lg hover:opacity-80 transition-colors"
            style={{ color: 'var(--color-text-muted, #898989)' }}
            title="Logout"
          >
            <LogOutLucideIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="glass-d-sidebar flex flex-col flex-shrink-0 overflow-hidden"
      style={{
        width: sidebarStyle.width,
        background: sidebarStyle.background,
      }}
    >
      {/* Header con Logo */}
      <div 
        className="flex items-center justify-between flex-shrink-0"
        style={{ 
          height: '4rem', 
          padding: '0 1rem',
          background: 'transparent'
        }}
      >
        <Link to="/boats" className="flex items-center">
          <img 
            src={((window as any).BOAT_TRADER_API?.plugin_url || '') + 'sealevel.webp' || '/wp-content/plugins/boat-trader-v3/assets/images/sealevel.webp'}
            alt="SeaLevel" 
            className="h-12 w-auto object-contain"
            width={120}
            height={48}
          />
        </Link>
        
        <button
          onClick={onToggle}
          className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title="Comprimi menu"
          aria-label="Comprimi menu"
        >
          <ChevronLeftIcon className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

       {/* User Info - Business Card Style */}
       <div className="px-4 py-3 bg-gradient-to-r from-sky-50 to-blue-50 flex-shrink-0">
         <div className="flex items-center gap-3">
           {/* Logo or Icon */}
           <div className={`w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden bg-white shadow-sm ${isAdmin ? 'border-2 border-amber-200' : 'border-2 border-sky-200'}`}>
              {user?.logo ? (
                <img 
                  src={fixImageUrl(user.logo)} 
                  alt={user?.company_name || 'Logo'} 
                  className="w-full h-full object-contain"
                  width={56}
                  height={56}
                />
             ) : isAdmin ? (
               <StarLucideIcon className="w-7 h-7 text-amber-600" />
             ) : (
               <BuildingLucideIcon className="w-7 h-7 text-sky-600" />
             )}
           </div>
           <div className="flex-1 min-w-0">
             <p className="text-sm font-bold text-gray-900 truncate">
               {isAdmin ? 'Admin' : (user?.company_name || 'Utente')}
             </p>
             {user?.citta && (
               <p className="text-xs text-gray-600 truncate flex items-center gap-1">
                 <MapPinLucideIcon className="w-3 h-3 text-sky-500" />
                 {user.citta}
               </p>
             )}
             {user?.phone && (
               <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                 <span>📞</span>
                 {user.phone}
               </p>
             )}
           </div>
         </div>
       </div>
       
       {/* Global Search */}
       <div className="px-3 py-2 flex-shrink-0">
         <GlobalSearch compact={true} />
       </div>

       {/* Navigation Sections */}
      <nav className="flex-1 overflow-y-auto py-4">
        {activeSections.map((section, idx) => (
          <div key={section.title} className={idx > 0 ? 'mt-4' : ''}>
            {/* Section Header */}
            <div className="px-4 mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: 'var(--color-text-muted, #898989)' }}>
                {section.title}
              </span>
              {section.collapsible && (
                <button
                  onClick={() => toggleSection(section.title.toLowerCase())}
                  className="p-1 rounded hover:bg-[var(--color-bg-secondary)]"
                  aria-label={expandedSections[section.title.toLowerCase()] ? `Comprimi ${section.title}` : `Espandi ${section.title}`}
                >
                  {expandedSections[section.title.toLowerCase()] ? (
                    <ChevronDownIcon className="w-3 h-3" style={{color:'var(--color-text-muted)'}} aria-hidden="true" />
                  ) : (
                    <ArrowRightLucideIcon className="w-3 h-3" style={{color:'var(--color-text-muted)'}} aria-hidden="true" />
                  )}
                </button>
              )}
            </div>

            {/* Section Items */}
            {(!section.collapsible || expandedSections[section.title.toLowerCase()] !== false) && (
              <div className="px-2 space-y-1">
                {section.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group`}
                    style={{
                      background: isActive(item.to) 
                        ? 'var(--glass-sidebar-active-bg, var(--color-primary, #0B3D6B20))'
                        : 'transparent',
                      color: isActive(item.to)
                        ? 'var(--glass-sidebar-active-text-color, var(--color-primary, #0B3D6B))'
                        : 'var(--glass-sidebar-text-color, var(--color-text, #242424))',
                    }}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.color || ''}`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium flex-1">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {isActive(item.to) && (
                      <ArrowRightLucideIcon className="w-4 h-4 text-sky-500" />
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer - Login/Logout */}
      <div className="p-3">
        {isPublic ? (
          <Link
            to="/login"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sky-600 hover:bg-sky-50 font-semibold transition-colors"
          >
            <UserLucideIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Accedi / Registrati</span>
          </Link>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOutLucideIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Esci</span>
          </button>
        )}
      </div>
    </div>
  );
}
